import { useState, useRef, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface SidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  width?: string;
  height?: string;
  position?: 'right' | 'left';
  showHandle?: boolean;
  enableDragToClose?: boolean;
}

export default function SidePanel({
  isOpen,
  onClose,
  title,
  icon,
  children,
  width = 'w-[450px]',
  height = 'h-[calc(100vh-3rem)]',
  position = 'right',
  showHandle = true,
  enableDragToClose = true,
}: SidePanelProps) {
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startY = useRef(0);

  useEffect(() => {
    const checkScrollLock = () => {
      // Only lock body scroll on mobile/tablet (overlay mode) - lg is 1024px
      const isMobile = window.matchMedia('(max-width: 1023px)').matches;
      if (isOpen && isMobile) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    };

    checkScrollLock();
    window.addEventListener('resize', checkScrollLock);
    
    return () => {
      window.removeEventListener('resize', checkScrollLock);
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setTranslateY(0);
    }
  }, [isOpen]);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!enableDragToClose) return;
    startY.current = e.touches[0].clientY;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!enableDragToClose || !isDragging) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;
    if (diff > 0) {
      setTranslateY(diff);
    }
  };

  const handleTouchEnd = () => {
    if (!enableDragToClose) return;
    setIsDragging(false);
    if (translateY > 120) {
      onClose();
    } else {
      setTranslateY(0);
    }
  };

  const positionClasses = position === 'right' 
    ? 'sticky top-6 ml-6' 
    : 'sticky top-6 mr-6';

  const widthClasses = position === 'right'
    ? 'w-0 opacity-0 ml-0'
    : 'w-0 opacity-0 mr-0';

  return (
    <>
      {/* Mobile Overlay/Drawer */}
      <div 
        className={`fixed inset-0 z-[100] lg:hidden transition-all duration-300 ${
          isOpen ? 'visible' : 'invisible'
        }`}
      >
        {/* Backdrop */}
        <div 
          className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
            isOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={onClose}
        />
        
        {/* Drawer Content */}
        <div 
          className={`absolute bottom-0 left-0 right-0 bg-zinc-900/40 backdrop-blur-xl rounded-t-xl transition-transform ${
            isDragging ? 'duration-0' : 'duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]'
          } transform ${
            isOpen ? '' : 'translate-y-full'
          } max-h-[90vh] overflow-hidden flex flex-col`}
          style={{ 
            transform: isOpen ? `translateY(${translateY}px)` : undefined,
            top: '10rem'
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Handle */}
          {showHandle && (
            <div className="w-12 h-1.5 bg-white/20 rounded-full mx-auto mt-3 mb-2 shrink-0 touch-none" />
          )}
          
          <div className="flex items-center justify-between p-6">
            <h3 className="text-lg font-semibold text-white flex items-center">
              {icon && <span className="mr-2 text-blue-400">{icon}</span>}
              {title}
            </h3>
          </div>
          
          <div className="flex-1 overflow-y-auto p-6 custom-scrollbar pb-12">
            {children}
          </div>
        </div>
      </div>

      {/* Desktop Push Panel */}
      <div 
        className={`${positionClasses} ${height} transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] overflow-hidden hidden lg:block ${
          isOpen ? `${width} opacity-100` : widthClasses
        }`}
      >
        <div className={`pointer-events-auto ${width} h-full transform transition ease-in-out duration-500 sm:duration-700 bg-zinc-900/40 backdrop-blur-xl rounded-xl flex flex-col overflow-hidden`}>
          <div className="flex h-full flex-col overflow-y-scroll py-6 px-6">
            <div className="flex items-center justify-between mb-6 pb-4">
              <h2 className="text-lg font-medium text-white flex items-center">
                {icon && <span className="mr-2 text-blue-400">{icon}</span>}
                {title}
              </h2>
              <button
                onClick={onClose}
                className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 transition-all"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1">
              {children}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}